import { defineStore } from "pinia"
import pick from "lodash/pick"
import http from "~/services/http"

interface IFetchProduct extends IErrorAndSuccessCallbacks {
  productSlug: string
  worksheetFileId?: string
}

function findChild(array: any[], searchId: number): any {
  let result = null

  // We use default for loop because it supports "break" keyword, where forEach does not
  for (let i = 0; i < array.length; i++) {
    const item = array[i]
    if (item.id === searchId) {
      result = item
      break
    } else if (item.children.length > 0) {
      const presentInChild = findChild(item.children, searchId)
      if (presentInChild) {
        result = presentInChild
        break
      }
    }
  }

  return result
}

export const usePdfStore = defineStore("pdfStore", {
  state: () => ({
    currentFile: null as any,
    currentPage: -1,
    currentSectionId: null as null | number,
  }),

  getters: {
    currentSectionObject(state) {
      if (state.currentSectionId) {
        return findChild(state.currentFile.table_of_contents, state.currentSectionId)
      } else {
        return null
      }
    },
  },

  actions: {
    flattenedArray(array: any[]) {
      let result = [] as any[]

      array.forEach((item) => {
        const pickedItem = pick(item, ["id", "page"])
        result.push(pickedItem)
        if (item.children.length > 0) {
          const children = this.flattenedArray(item.children)
          result = result.concat(children)
        }
      })

      return result
    },

    recursiveChildrenSeeker(arrayToSeek: any[]): any {
      if (this.currentSectionId === null) {
        return []
      }

      let result = null

      for (let i = 0; i < arrayToSeek.length; i++) {
        const item = arrayToSeek[i]
        if (item.id === this.currentSectionId) {
          result = [item.id]
          break
        } else if (item.children.length > 0) {
          const childResult = this.recursiveChildrenSeeker(item.children)
          if (childResult !== null) {
            childResult.unshift(item.id)
            result = childResult
            break
          }
        }
      }

      return result
    },

    intervalUpperLimit(id: number) {
      const flattenedContents = this.flattenedArray(this.currentFile.table_of_contents)
      const sectionIdx = flattenedContents.findIndex((section) => section.id === id)

      if (sectionIdx + 1 === flattenedContents.length) {
        return -1
      } else {
        return flattenedContents[sectionIdx + 1].page
      }
    },

    fetchFile({ productSlug, worksheetFileId, successCallback, errorCallback }: IFetchProduct) {
      // Reset to defaults
      this.currentPage = -1
      this.currentSectionId = null

      http.get({
        url: `v1/products/pdf-book-file/${productSlug}`,
        params: {
          worksheet_file: worksheetFileId ?? null,
        },
        successCallback: (ctx) => {
          this.currentFile = ctx.response._data
          successCallback && successCallback(ctx)
        },
        errorCallback: (ctx) => {
          errorCallback && errorCallback(ctx)
        },
      })
    },
  },
})
